import React, { useEffect, useState } from 'react';
import { tasksApi } from '../api/tasks';
import TaskCreate from '../pages/Task/TaskCreate';

interface Entity {
  id: string;
  linked_headOfficeID?: string | null;
}

interface Props {
  entity: Entity;
  kind: 'client' | 'vendor';
}

interface TaskRow {
  id: string;
  title: string;
  status: string;
  priority?: string | null;
  due_date?: string | null;
}

export default function EntityTasks({ entity, kind }: Props) {
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const isLinked = !!entity.linked_headOfficeID;

  const load = async () => {
    if (!isLinked || !entity.linked_headOfficeID) {
      setTasks([]);
      return;
    }
    setLoading(true);
    try {
      const res = await tasksApi.listOriginatedByMe(entity.linked_headOfficeID);
      setTasks(Array.isArray(res.data) ? res.data : []);
    } catch {
      setTasks([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity.id, entity.linked_headOfficeID]);

  return (
    <div className="entity-tasks p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-md font-semibold">
          Tasks {kind === 'vendor' ? 'sent to this vendor' : 'with this client'}
        </h3>
        {isLinked && (
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm">
            Create Task
          </button>
        )}
      </div>

      {loading ? (
        <p className="text-gray-500 text-sm">Loading…</p>
      ) : !isLinked ? (
        <p className="text-gray-500 text-sm">
          This {kind} is not cross-org linked, so cross-org tasks aren't available.
        </p>
      ) : tasks.length === 0 ? (
        <p className="text-gray-500 text-sm">No tasks yet.</p>
      ) : (
        <ul className="divide-y border rounded">
          {tasks.map(t => (
            <li key={t.id} className="px-3 py-2 flex justify-between text-sm">
              <span>{t.title}</span>
              <span className="text-gray-500">{t.status}</span>
            </li>
          ))}
        </ul>
      )}

      {creating && entity.linked_headOfficeID && (
        <TaskCreate
          isOpen={true}
          mode="cross-org-draft"
          targetHeadOfficeId={entity.linked_headOfficeID}
          onClose={() => setCreating(false)}
          onSuccess={() => { setCreating(false); load(); }}
        />
      )}
    </div>
  );
}
